import { createContext, useContext, useState, useCallback } from 'react';

/**
 * Veg Mode — a device-local "show only vegetarian items" preference.
 *
 * Persisted to localStorage (key `pbc_veg_mode`), NOT the server: it's a
 * per-device browsing toggle (like Zomato's), so it doesn't need to follow the
 * user across devices and needs no backend. Menu.js reads `vegMode` and, when
 * true, hard-filters its list to items WITHOUT the `Non Veg` tag (the same
 * predicate the existing "Veg"/"Non Veg" quick filters use). Profile.js renders
 * the toggle row. Lazy-init from storage; every change writes through.
 */
const VEG_MODE_KEY = 'pbc_veg_mode';

function readInitial() {
  try {
    return localStorage.getItem(VEG_MODE_KEY) === '1';
  } catch {
    // Private browsing / SSR / disabled storage — default OFF, keep in memory.
    return false;
  }
}

const VegModeContext = createContext({
  vegMode: false,
  setVegMode: () => {},
  toggleVegMode: () => {},
});

export function VegModeProvider({ children }) {
  const [vegMode, setVegModeState] = useState(readInitial);

  // Accepts a boolean or an updater fn; writes through to localStorage.
  const setVegMode = useCallback((next) => {
    setVegModeState((prev) => {
      const value = typeof next === 'function' ? !!next(prev) : !!next;
      try {
        if (value) localStorage.setItem(VEG_MODE_KEY, '1');
        else localStorage.removeItem(VEG_MODE_KEY);
      } catch {
        /* quota / private mode — in-memory only for this session */
      }
      return value;
    });
  }, []);

  const toggleVegMode = useCallback(() => setVegMode((v) => !v), [setVegMode]);

  return (
    <VegModeContext.Provider value={{ vegMode, setVegMode, toggleVegMode }}>
      {children}
    </VegModeContext.Provider>
  );
}

/** Read `{ vegMode, setVegMode, toggleVegMode }`. Used by: Profile.js, Menu.js. */
export function useVegMode() {
  return useContext(VegModeContext);
}
